import React, { useEffect, useRef, useState } from 'react';

// Utilise l'API BarcodeDetector native du navigateur (Chrome/Android)
// Fallback : saisie manuelle si non supporté

export default function BarcodeScanner({ onDetected, onClose }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const detectorRef = useRef(null);
  const animRef = useRef(null);
  const [status, setStatus] = useState('starting');
  const [supported, setSupported] = useState(true);

  useEffect(() => {
    if (!('BarcodeDetector' in window)) {
      setSupported(false);
      setStatus('unsupported');
      return;
    }

    detectorRef.current = new window.BarcodeDetector({
      formats: ['code_128', 'code_39', 'ean_13', 'ean_8', 'qr_code', 'data_matrix', 'pdf417'],
    });

    navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
    }).then(stream => {
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        setStatus('scanning');
        startDetection();
      }
    }).catch(() => {
      setStatus('error');
    });

    return () => {
      stopCamera();
    };
  }, []);

  function stopCamera() {
    if (animRef.current) cancelAnimationFrame(animRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
  }

  function startDetection() {
    const detect = async () => {
      if (!videoRef.current || videoRef.current.readyState < 2) {
        animRef.current = requestAnimationFrame(detect);
        return;
      }
      try {
        const barcodes = await detectorRef.current.detect(videoRef.current);
        if (barcodes.length > 0) {
          const value = barcodes[0].rawValue;
          stopCamera();
          onDetected(value);
          return;
        }
      } catch {}
      animRef.current = requestAnimationFrame(detect);
    };
    animRef.current = requestAnimationFrame(detect);
  }

  if (!supported) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ background: '#fff', borderRadius: 16, padding: 24, textAlign: 'center', maxWidth: 320 }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>📷</div>
          <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Scanner non supporté</p>
          <p style={{ fontSize: 13, color: 'var(--texte-doux)', marginBottom: 16 }}>Le scan de code-barre nécessite Chrome sur Android. Saisis le numéro manuellement.</p>
          <button onClick={onClose} style={{ width: '100%', padding: 12, background: 'var(--accent-plein)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>Fermer</button>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ background: '#fff', borderRadius: 16, padding: 24, textAlign: 'center', maxWidth: 320 }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
          <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Accès caméra refusé</p>
          <p style={{ fontSize: 13, color: 'var(--texte-doux)', marginBottom: 16 }}>Autorise l'accès à la caméra dans les paramètres du navigateur.</p>
          <button onClick={onClose} style={{ width: '100%', padding: 12, background: 'var(--accent-plein)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>Fermer</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#000', zIndex: 1000, display: 'flex', flexDirection: 'column' }}>
      <video ref={videoRef} style={{ flex: 1, width: '100%', objectFit: 'cover' }} muted playsInline />
      
      {/* Viseur */}
      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 280, height: 120 }}>
        {/* coins du viseur */}
        {[
          { top: 0, left: 0, borderTop: '3px solid #fff', borderLeft: '3px solid #fff' },
          { top: 0, right: 0, borderTop: '3px solid #fff', borderRight: '3px solid #fff' },
          { bottom: 0, left: 0, borderBottom: '3px solid #fff', borderLeft: '3px solid #fff' },
          { bottom: 0, right: 0, borderBottom: '3px solid #fff', borderRight: '3px solid #fff' },
        ].map((style, i) => (
          <div key={i} style={{ position: 'absolute', width: 24, height: 24, borderRadius: 2, ...style }} />
        ))}
        {/* ligne de scan animée */}
        <div style={{
          position: 'absolute', left: 8, right: 8, height: 2, background: 'var(--accent)',
          top: '50%', transform: 'translateY(-50%)',
          boxShadow: '0 0 8px var(--accent)',
          animation: 'scan 1.5s ease-in-out infinite alternate',
        }} />
      </div>

      <style>{`
        @keyframes scan {
          from { top: 20%; }
          to { top: 80%; }
        }
      `}</style>

      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '24px 24px 40px', background: 'linear-gradient(transparent, rgba(0,0,0,0.8))' }}>
        <p style={{ color: '#fff', textAlign: 'center', fontSize: 14, marginBottom: 20 }}>
          {status === 'starting' ? 'Démarrage de la caméra…' : 'Pointe le code-barre dans le cadre'}
        </p>
        <button onClick={() => { stopCamera(); onClose(); }} style={{ width: '100%', padding: 14, background: 'rgba(255,255,255,0.2)', color: '#fff', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 12, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
          Annuler
        </button>
      </div>
    </div>
  );
}
